import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { Button } from '@components/Core/Button';
import { UploadCSVInput } from '@inputs/UploadCSVInput';
import { VerificationCodeFormTransFields } from '@forms/VerificationCodeForm/types';
import { verificationCodeFormTranslations } from '@forms/VerificationCodeForm/translations';
import { ChangeProfileNamePopupTransFields } from '@popups/ChangeProfileNamePopup/types';
import { changeProfileNamePopupTranslations } from '@popups/ChangeProfileNamePopup/translations';
import { StatusPopupTransFields } from '@popups/StatusPopup/types';
import { statusPopupTranslations } from '@popups/StatusPopup/translations';
import { Obj } from '@/types/common';
import { uploadCSVPopupTranslations } from './translations';
import { IUploadCSVPopupProps, UploadCSVPopupTransFields } from './types';

export function UploadCSVPopup<T extends Obj>({
  defaultValues,
  onSubmit,
  error,
  disabled = false,
  name = 'file',
}: IUploadCSVPopupProps<T>) {
  const {
    popupStore: { closePopup },
  } = useStore();

  const { t } = useMyTranslations<
    | UploadCSVPopupTransFields
    | Extract<VerificationCodeFormTransFields, 'Confirm'>
    | Extract<ChangeProfileNamePopupTransFields, 'Cancel'>
    | Extract<StatusPopupTransFields, 'btnText'>
  >([
    uploadCSVPopupTranslations.name,
    verificationCodeFormTranslations.name,
    changeProfileNamePopupTranslations.name,
    statusPopupTranslations.name,
  ]);

  const useFormMethods = useForm<T>({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const { handleSubmit } = useFormMethods;

  const onSubmitHandler = useCallback((data: T) => {
    onSubmit(data);
  }, []);

  return (
    <div className="flex flex-col justify-stretch items-center">
      <p className="default-text mb-[24px]">{t('title')}</p>
      {error ? (
        <>
          <p className={`default-text text-error text-center mb-[24px]`}>{error}</p>
          <Button
            text={t('btnText', { ns: statusPopupTranslations.name })}
            onClick={closePopup}
            styleProp="mt-[32px]"
          />
        </>
      ) : (
        <>
          <p className={`default-text text-[#414042CC] text-center mb-[24px]`}>
            {t('description')}
          </p>
          {/* // use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error
    // @ts-ignore */}
          <FormProvider {...useFormMethods}>
            <UploadCSVInput name={name} />
            <Button
              text={t('Confirm')}
              onClick={handleSubmit(onSubmitHandler)}
              styleProp="mt-[32px]"
              disabled={disabled}
            />
            <Button
              text={t('Cancel')}
              onClick={closePopup}
              styleProp="mt-[12px]"
              styleType="secondary"
            />
          </FormProvider>
        </>
      )}
    </div>
  );
}
